import React from "react";
import { Activity } from "lucide-react";

/**
 * Tap-tempo button. Tap in rhythm — BPM is computed from the rolling average of the
 * last few intervals (window: 8). Auto-resets after a 2.5s pause.
 *
 * Props:
 *  - onBpm(bpm: number) : called with the current computed BPM after each tap.
 *  - resetMs (default 2500) : silence after which the buffer resets.
 *  - maxTaps (default 8) : rolling window.
 *  - className : container className.
 */
export const TapTempoButton = ({ onBpm, resetMs = 2500, maxTaps = 8, className = "" }) => {
  const tapsRef = React.useRef([]); // timestamps (ms)
  const [bpm, setBpm] = React.useState(null);
  const [pulse, setPulse] = React.useState(0);
  const resetTimerRef = React.useRef(null);

  const handleTap = () => {
    const now = performance.now();
    const taps = tapsRef.current;
    // reset if last tap too far in the past
    if (taps.length > 0 && now - taps[taps.length - 1] > resetMs) {
      tapsRef.current = [];
    }
    tapsRef.current = [...tapsRef.current.slice(-(maxTaps - 1)), now];
    const arr = tapsRef.current;
    if (arr.length >= 2) {
      const intervals = [];
      for (let i = 1; i < arr.length; i++) intervals.push(arr[i] - arr[i - 1]);
      const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      if (avg > 0) {
        const computed = Math.max(20, Math.min(300, 60000 / avg));
        const rounded = Math.round(computed * 10) / 10;
        setBpm(rounded);
        onBpm?.(rounded);
      }
    }
    // pulse visual
    setPulse((p) => p + 1);
    // schedule auto-reset
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      tapsRef.current = [];
      setBpm(null);
    }, resetMs);
  };

  React.useEffect(() => () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
  }, []);

  const tapsCount = tapsRef.current.length;

  return (
    <button
      type="button"
      onClick={handleTap}
      onTouchStart={(e) => { e.preventDefault(); handleTap(); }}
      className={`relative overflow-hidden tap-lg w-full rounded-xl bg-[#161616] border border-white/10 hover:border-[var(--gl-gold)]/50 active:scale-[0.98] transition flex items-center justify-between px-4 py-3 ${className}`}
      data-testid="tap-tempo-btn"
    >
      <span
        key={pulse}
        className="absolute inset-0 bg-[var(--gl-gold)]/15 animate-[ping_0.4s_ease-out_1] pointer-events-none"
        aria-hidden="true"
      />
      <span className="flex items-center gap-2 relative">
        <Activity size={18} className="text-[var(--gl-gold)]" />
        <span className="text-sm font-bold uppercase tracking-wider">Tap tempo</span>
      </span>
      <span className="flex items-center gap-3 relative">
        <span className="text-xs text-neutral-400 tabular-nums">
          {tapsCount > 0 ? `${tapsCount} tap${tapsCount > 1 ? "s" : ""}` : "Tape en rythme"}
        </span>
        <span
          className="text-[var(--gl-gold)] font-display text-2xl tabular-nums w-20 text-right"
          data-testid="tap-tempo-bpm"
        >
          {bpm != null ? bpm.toFixed(1) : "—"}
        </span>
      </span>
    </button>
  );
};

export default TapTempoButton;
